import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Api_plateforme, api_collection_plateform } from '@leandredev/adaptivai-ng16-lib/httpClient/services/api';
import { Authentication } from '@leandredev/adaptivai-ng16-lib/httpClient/services/authentication';

import { UserMessages } from '@leandredev/adaptivai-ng16-lib/httpClient/services/UserMessages';
import { Document } from '@leandredev/adaptivai-ng16-lib/httpClient/services/Document';

import { Admin } from '@leandredev/adaptivai-ng16-lib/httpClient/services/Admin';
import { UserClient } from '@leandredev/adaptivai-ng16-lib/httpClient/services/UserClient';
import { FileService } from '@leandredev/adaptivai-ng16-lib/httpClient/services/FileService';
import { TagService } from '@leandredev/adaptivai-ng16-lib/httpClient/services/TagService';
import { DataPrivacy } from '@leandredev/adaptivai-ng16-lib/httpClient/services/DataPrivacy';
import { SharedLibrarySubscription } from '@leandredev/adaptivai-ng16-lib/httpClient/services/SharedLibrarySubscription';
import { SharedLibrary } from '@leandredev/adaptivai-ng16-lib/httpClient/services/SharedLibrary';
import { HttpServiceBase, ApiHelper } from '@leandredev/common-ng16/httpClient';
import { RunTimeDataService } from '@leandredev/common-ng16/infraService';
import { interfaces } from '@leandredev/adaptivai-common-lib';

interface ProxyData {
  [key: string]: { [key: string]: unknown };
}

interface ProxyObservable {
  [key: string]: { [key: string]: Observable<unknown> };
}

interface PublicConfig {
  api: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  public api: Api_plateforme;
  public authService: Authentication;

  public userMessage: UserMessages;
  public documents: Document;
  public fileService: FileService;
  public admin: Admin;
  public userClient: UserClient;
  public tagService: TagService;
  public dataPrivacy: DataPrivacy;

  public sharedLibrarySubscriptionService: SharedLibrarySubscription;
  public sharedLibrary: SharedLibrary;

  private readonly adminProfile: BehaviorSubject<interfaces.IUserAdmin | null> = new BehaviorSubject<interfaces.IUserAdmin | null>(null);
  public readonly profile: Observable<interfaces.IUserAdmin | null>;

  public uploadUrl = '';
  public avatarUrl = '';
  public fileUploadServiceUrl = '';

  private proxyObj: ProxyData = {};
  private proxyObservable: ProxyObservable = {};

  constructor(
    private readonly http: HttpClient,
    private readonly runTimeDataService: RunTimeDataService
  ) {
    this.profile = this.adminProfile.asObservable();

    // Initialize services
    this.api = new Api_plateforme(this.http);
    this.authService = new Authentication(this.http, false);

    this.userMessage = new UserMessages(this.http, true);
    this.documents = new Document(this.http, true);
    this.fileService = new FileService(this.http, true);
    this.admin = new Admin(this.http, true);
    this.userClient = new UserClient(this.http, true);
    this.tagService = new TagService(this.http, true);
    this.dataPrivacy = new DataPrivacy(this.http, true);

    this.sharedLibrarySubscriptionService = new SharedLibrarySubscription(this.http, true);
    this.sharedLibrary = new SharedLibrary(this.http, true);

    // Subscribe to configuration updates
    this.runTimeDataService.getObservableStore('publicConf').subscribe({
      next: (config: PublicConfig | null) => {
        if (config?.api) {
          this.updateServiceUrls(config.api);
        }
      },
      error: (err: unknown) => console.error('Failed to load publicConf:', err)
    });

    this.runTimeDataService.getObservableStore('ConfService').subscribe({
      next: (profile: interfaces.IUserAdmin | null) => {
        if (profile) {
          this.adminProfile.next(profile);
        }
      },
      error: (err: unknown) => console.error('Failed to load ConfService:', err)
    });
  }

  private updateServiceUrls(apiBaseUrl: string): void {
    this.authService.urlCollection = `${apiBaseUrl}authentication/`;
    this.userMessage.urlCollection = `${apiBaseUrl}userMessages/`;

    this.documents.urlCollection = `${apiBaseUrl}documentloader/`;
    this.fileService.urlCollection = `${apiBaseUrl}fileservice/`;
    this.admin.urlCollection = `${apiBaseUrl}admin/`;
    this.userClient.urlCollection = `${apiBaseUrl}userclient/`;
    this.tagService.urlCollection = `${apiBaseUrl}tagService/`;
    this.dataPrivacy.urlCollection = `${apiBaseUrl}dataPrivacy/`;

    this.sharedLibrarySubscriptionService.urlCollection = `${apiBaseUrl}sharedLibrarySubscription/`;
    this.api.urlApi = `${apiBaseUrl}bdd/`;
    this.fileUploadServiceUrl = `${apiBaseUrl}fileService/`;
    this.sharedLibrary.urlCollection = `${apiBaseUrl}sharedLibrary`;
  }

  public getProxyData<T>(columnName: keyof api_collection_plateform, idObj: string): Observable<T | null> {
    const colName = columnName as string;

    // Check cache first
    if (this.proxyObj[colName]?.[idObj]) {
      return of(this.proxyObj[colName][idObj] as T);
    }

    // Check if request is already in progress
    if (this.proxyObservable[colName]?.[idObj]) {
      return this.proxyObservable[colName][idObj] as Observable<T>;
    }

    // Initialize cache objects if they don't exist
    this.proxyObj[colName] = this.proxyObj[colName] ?? {};
    this.proxyObservable[colName] = this.proxyObservable[colName] ?? {};

    // Make API call
    const caller = this.api.collections[columnName] as HttpServiceBase<T>;
    this.proxyObservable[colName][idObj] = caller.get(idObj).pipe(
      map((dataRes) => {
        delete this.proxyObservable[colName][idObj];
        if (dataRes.code === 200 && dataRes.response.length > 0) {
          this.proxyObj[colName][idObj] = dataRes.response[0];
          return dataRes.response[0] as T;
        }
        return null;
      })
    );

    return this.proxyObservable[colName][idObj] as Observable<T>;
  }

  public getIcon(icon: string): Observable<interfaces.IAdaptivIcon | null> {
    return this.api.collections.AdaptivIcon.get(icon).pipe(ApiHelper.resultToObj<interfaces.IAdaptivIcon>('getIcon'));
  }

  public getLlmActions(): Observable<interfaces.ILlmAction[]> {
    return this.api.collections.LlmAction.get('*').pipe(ApiHelper.resultToArr<interfaces.ILlmAction>('getLlmActions'));
  }
}
